import { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useIntl } from "react-intl";
import { HasPermission } from 'Core/security';

//Components
import { Modal, ModalBody, ModalFooter, Button } from "@gpstrackit/react-controls";
import { NotificationManager } from "Components/Notifications";
import CustomCard from 'Components/Card';
import CustomSelect from 'Components/Select';
import FiberNew from '@mui/icons-material/FiberNew';
import Summary from 'Components/Summary';
import {
    Icon,
    OutlinedInput,
    InputAdornment,
    FormControl,
} from '@mui/material';
import AddCircle from '@mui/icons-material/AddCircle';

//Actions
import {
    alertTypesDeviceModelRequest,
    alertTypesUpdateRequest,
    alertTypesDetailsRequest,
    clearRedux
} from 'Redux/actions'

import {
    getDeviceModels
} from 'Modules/alertTypes/views/main/.components/alertTypeDetails/redux/saga';

const AlertTypeDetails = () => {
    const { messages } = useIntl();
    const dispatch = useDispatch();

    //Local States
    const [editMode, setEditMode] = useState(false);
    const [alert, setAlert] = useState(null);
    const [devicesModels, setDeviceModels] = useState([]);
    const [devicesModel, setDeviceModel] = useState(null);
    const [openSearch, setOpenSearch] = useState("");
    const [alertTypeId, setAlertTypeId] = useState(null);
    const [deleteChanges, setDeleteChanges] = useState(false);

    //Redux States
    const { loading, openModal, result, alertType, devicesModels: list, update } = useSelector(state => state.alertTypeDetailsRedux);
    const user = useSelector(state => state.securityRedux.user);

    const closeModal = () => {
        setEditMode(false)
        dispatch(clearRedux("ALERT_TYPES_DETAILS"))
        setDeviceModels([])
        setDeviceModel(null)
        setOpenSearch(null)
        setAlertTypeId(null)
        setDeleteChanges(false)
        setAlert(null)
    }

    const applyChanges = () => {
        let currentDeviceModels = devicesModels.length ? devicesModels.filter((item) => !item.deleted) : []
        let ids = currentDeviceModels.map((item) => item?.id)
        let payload = {
            id: alertType?.id,
            deviceModelIds: ids
        }
        dispatch(alertTypesUpdateRequest(payload))
    }

    const insertDeviceModel = (item) => {
        let currentDeviceModels = devicesModels
        let deviceModelValidation = currentDeviceModels.some((dm) => dm?.id === item?.id)
        if (deviceModelValidation) {
            NotificationManager.warning(
                messages['alert_details_edit_alert_type_warning'],
                messages['alert_details_edit_alert_type'],
                3000
            );
        } else {
            currentDeviceModels.unshift({ ...item, new: true, deleted: false })
            setDeviceModels([...currentDeviceModels])
        }
        setDeviceModel(null);
    }

    const removeDuplicates = (arr) => {
        let unique = {};
        let result = [];
        for (let i = 0; i < arr.length; i++) {
            if (!unique[arr[i].id]) {
                result.push(arr[i]);
                unique[arr[i].id] = true;
            }
        }
        return result;
    }

    const filterValues = (newList, currentList) => {
        const currentDeviceModels = new Set(currentList.map(obj => obj.id));
        const result = newList.filter(obj => !currentDeviceModels.has(obj.id));
        return result;
    }

    const getDeviceModelsForSelect = (input, callback) => {
        if (!input) {
            callback([])
            return
        }
        getDeviceModels(input).then((response) => {
            if (response.result) {
                const { items } = response?.result
                let newList = removeDuplicates(items)
                let deviceModelList = filterValues(newList, devicesModels)
                callback(deviceModelList)
            }
        })
    }

    useEffect(() => {
        if (result) {
            setAlert(result)
            dispatch(alertTypesDeviceModelRequest(result?.id, openSearch))
            setAlertTypeId(result?.id)
            dispatch(clearRedux("ALERT_TYPES_DETAILS", { result: null }))
        }
        if (alertType?.id && openModal && !alert) {
            dispatch(alertTypesDetailsRequest(alertType?.id))
        }
    }, [alertType, result, openModal])

    useEffect(() => {
        if (list) {
            let newList = list?.map((item) => ({ ...item?.deviceModel, deleted: false }))
            setDeviceModels(newList)
        }
    }, [list])

    useEffect(() => {
        if (update) {
            if (update?.id) {
                NotificationManager.success(
                    messages['alert_details_edit_alert_type_success'],
                    messages['alert_details_edit_alert_type'],
                    3000
                );
                dispatch(alertTypesDeviceModelRequest(update?.id, openSearch))
                setAlertTypeId(update?.id)
                setEditMode(false)
            } else if (update?.error) {
                NotificationManager.error(
                    messages['alert_details_edit_alert_type_error'],
                    messages['alert_details_edit_alert_type'],
                    3000
                );
            }
            dispatch(clearRedux("ALERT_TYPES_DETAILS", { update: null }))
        }
    }, [update])

    useEffect(() => {
        if (openSearch || openSearch === "") {
            const timer = setTimeout(() => {
                dispatch(alertTypesDeviceModelRequest(alertTypeId, openSearch))
            }, 500)
            return () => clearTimeout(timer);
        }
    }, [openSearch])

    return (
        <Modal
            open={openModal}
            title={`${editMode ? `${messages['alert_details_edit']} - ` : ""}${alertType?.name || ''}`}
            handleClose={() => closeModal()}
            position="right"
            size="small"
            loading={loading}
        >
            <ModalBody>
                {
                    deleteChanges ?
                        <div style={{ padding: '0 10px', marginBottom: 40 }}>
                            <span>{messages['alert_details_confirm_delete']}</span>
                        </div>

                        : <div style={{ padding: '0 10px', marginBottom: 40 }}>
                            {alert && !editMode &&
                                <>
                                    <Summary data={alert} columns={2} />
                                </>
                            }
                            {
                                editMode &&
                                <div style={{ marginBottom: 10 }}>
                                    <CustomSelect
                                        async
                                        value={devicesModel}
                                        onChange={(items) => insertDeviceModel(items)}
                                        label={messages['alert_details_device_model']}
                                        loadOptions={getDeviceModelsForSelect}
                                        loading={false}
                                        required
                                        getOptionLabel={e => `${e.name} (${e.manufacturer?.name})`}
                                        getOptionValue={e => e.id}
                                        placeholder={messages['alert_details_placeholder']}
                                    />
                                </div>
                            }
                            <div>
                                <div className="summary-item-key" style={{ marginTop: 5, color: '#6c757d', fontWeight: 400 }}>{messages['alert_details_units_supporting']}</div>
                                {
                                    !editMode ?
                                        <div style={{ margin: '6px 0' }}>
                                            <FormControl style={{ width: '100%' }}>
                                                <OutlinedInput
                                                    style={{ marginTop: 2, height: 36, fontSize: 'inherit', color: '#212529' }}
                                                    className={`imports-search-accounts`}
                                                    disabled={false}
                                                    id="custom-search"
                                                    placeholder={messages['alert_details_search_device_model']}
                                                    type="text"
                                                    value={openSearch || ''}
                                                    fullWidth={true}
                                                    onChange={(event) => setOpenSearch(event.target.value)}
                                                    required
                                                    endAdornment={
                                                        <InputAdornment position="end">
                                                            <div style={{
                                                                height: 40,
                                                                width: 34,
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                cursor: 'pointer'
                                                            }}
                                                                onClick={() => { }}
                                                            >
                                                                <Icon>search</Icon>
                                                            </div>
                                                        </InputAdornment>
                                                    }
                                                />
                                            </FormControl>
                                        </div> : <></>
                                }
                                <div style={{ height: editMode ? '66vh' : '54vh', overflow: 'auto', padding: 2 }}>
                                    {devicesModels?.length ? devicesModels.map((item, index) => {
                                        return (
                                            <CustomCard
                                                key={index}
                                                hideButton={!editMode}
                                                onClick={() => {
                                                    let currentDeviceModels = devicesModels
                                                    let index = devicesModels.findIndex((dm) => dm.id === item.id)
                                                    if (index > -1) {
                                                        devicesModels[index].deleted = !item.deleted
                                                    }
                                                    setDeviceModels([...currentDeviceModels])
                                                }}
                                                styles={{ marginLeft: 1, backgroundColor: item.deleted && '#efededcc', color: item.deleted && '#ababab' }}
                                                customMessage={item.deleted && messages['alert_details_add_back']}
                                                customIcon={item.deleted ? () => {
                                                    return (
                                                        <AddCircle />
                                                    )
                                                } : null}
                                            >
                                                <div style={{ display: 'flex', alignItems: 'center' }}>
                                                    <div style={{ width: '90%' }}>
                                                        <div>
                                                            <label style={{ fontWeight: 700, marginRight: 5 }}>{messages['alert_details_device_model']}:</label><span>{item?.name}</span>
                                                        </div>
                                                        <div>
                                                            <label style={{ fontWeight: 700, marginRight: 5 }}>{messages['alert_details_manufacturer']}:</label><span>{item?.manufacturer?.name}</span>
                                                        </div>
                                                    </div>
                                                    {
                                                        item.new && editMode ? (
                                                            <div style={{ width: '10%' }}>
                                                                <FiberNew style={{ color: '#66bb6a' }} />
                                                            </div>
                                                        ) : <></>
                                                    }
                                                </div>
                                            </CustomCard>
                                        )
                                    }) : <div style={{ display: 'flex', justifyContent: 'center' }}>{messages['alert_details_no_data']}</div>}
                                </div>
                            </div>
                        </div>
                }
            </ModalBody>
            <ModalFooter>
                <Button
                    onClick={() => {
                        if (deleteChanges) {
                            setDeleteChanges(false)
                        } else {
                            if (editMode) {
                                let currentDeviceModels = devicesModels.some((item) => item.deleted)
                                if (currentDeviceModels) {
                                    setDeleteChanges(true)
                                } else {
                                    setEditMode(false)
                                }
                            } else {
                                closeModal()
                            }
                        }

                    }}
                    className={"btn-cancel"}
                >
                    {editMode ? messages['cancel'] : messages['close']}
                </Button>
                <Button
                disabled={!HasPermission(user, "alrttp", "u")}
                    onClick={() => {
                        if (editMode && !deleteChanges) {
                            applyChanges()
                        } else if (deleteChanges) {
                            let newList = list?.map((item) => ({ ...item?.deviceModel, deleted: false }))
                            setDeviceModels(newList)
                            setDeleteChanges(false)
                            setEditMode(false)
                        } else {
                            setEditMode(true)
                            setOpenSearch("")
                        }
                    }}
                    className="btn-dark-blue"
                >
                    {editMode ? deleteChanges ? messages['common_continue'] : messages['alert_details_save'] : deleteChanges ? messages['common_back'] : messages['common_edit_assign']}
                </Button>
            </ModalFooter>
        </Modal>
    )
}

export default AlertTypeDetails;